import { useEffect, useMemo, useState } from 'react';
import { useStrings } from '@/i18n/Localization';
import { formatBytes } from '@/media/format';
import type { UploadRecord, UploadState } from '@/store/types';
import type { Hex } from '@/store/ids';
import styles from '@/components/media.module.css';

export interface UploadListProps {
  uploads: readonly UploadRecord[];
  onPause: (uploadId: Hex) => void;
  onResume: (uploadId: Hex) => void;
  onCancel: (uploadId: Hex) => void;
}

/**
 * The Section 14 upload item: name and preview, confirmed progress,
 * pause/resume, cancel, and retry on error.
 *
 * The progress figure is `bytesConfirmed / sizeBytes` and nothing else. That
 * number only moves when the server has acknowledged a part, which is the
 * Section 06 requirement stated as a UI property: a stalled connection shows a
 * bar that stops rather than one that fills while nothing arrives.
 */
export function UploadList(props: UploadListProps) {
  const { t } = useStrings();

  if (props.uploads.length === 0) {
    return null;
  }

  return (
    <ul className={styles.uploadList} aria-label={t('upload.list')}>
      {props.uploads.map((upload) => (
        <UploadItem
          key={upload.uploadId}
          upload={upload}
          onPause={props.onPause}
          onResume={props.onResume}
          onCancel={props.onCancel}
        />
      ))}
    </ul>
  );
}

function stateLabelKey(state: UploadState) {
  switch (state) {
    case 'CREATED':
      return 'upload.waiting' as const;
    case 'UPLOADING':
      return 'upload.uploading' as const;
    case 'VERIFYING':
      return 'upload.verifying' as const;
    case 'READY':
      return 'upload.ready' as const;
    case 'PAUSED':
      return 'upload.paused' as const;
    case 'EXPIRED':
      return 'upload.expired' as const;
    default:
      return 'upload.failed' as const;
  }
}

function UploadItem({
  upload,
  onPause,
  onResume,
  onCancel,
}: {
  upload: UploadRecord;
  onPause: (uploadId: Hex) => void;
  onResume: (uploadId: Hex) => void;
  onCancel: (uploadId: Hex) => void;
}) {
  const { t } = useStrings();
  const [thumbnail, setThumbnail] = useState<string | null>(null);

  // A local preview of the bytes still on the device. It is revoked on unmount
  // and whenever the blob is dropped, because an object URL that outlives its
  // component keeps the whole file alive in memory.
  useEffect(() => {
    const blob = upload.blob;
    if (!blob || !upload.mime.startsWith('image/')) {
      setThumbnail(null);
      return;
    }
    const url = URL.createObjectURL(blob);
    setThumbnail(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [upload.blob, upload.mime]);

  const percent = useMemo(() => {
    if (upload.sizeBytes <= 0) {
      return upload.state === 'READY' ? 100 : 0;
    }
    return Math.min(100, Math.round((upload.bytesConfirmed / upload.sizeBytes) * 100));
  }, [upload.bytesConfirmed, upload.sizeBytes, upload.state]);

  const active = upload.state === 'UPLOADING' || upload.state === 'CREATED';
  const resumable = upload.state === 'PAUSED' || upload.state === 'FAILED';

  return (
    <li className={styles.uploadItem}>
      {thumbnail === null ? null : (
        // Decorative: the filename beside it already names the attachment, so
        // an alt text here would be read twice.
        <img className={styles.uploadThumb} src={thumbnail} alt="" />
      )}

      <div className={styles.uploadBody}>
        <span className={styles.uploadName} dir="auto">
          {upload.filename}
        </span>
        <span className={styles.uploadMeta}>
          {t(stateLabelKey(upload.state))}
          {' · '}
          {t('upload.progress', { percent, size: formatBytes(upload.sizeBytes) })}
        </span>
        {upload.lastError !== null && upload.state === 'FAILED' ? (
          <span className={styles.uploadError} role="status">
            {upload.lastError}
          </span>
        ) : null}
        <progress
          className={styles.progress}
          max={100}
          value={percent}
          aria-label={t('upload.progress', { percent, size: formatBytes(upload.sizeBytes) })}
        />
      </div>

      <div className={styles.uploadActions}>
        {active ? (
          <button
            type="button"
            className={styles.uploadAction}
            onClick={() => {
              onPause(upload.uploadId);
            }}
          >
            {t('upload.pause')}
          </button>
        ) : null}
        {resumable ? (
          <button
            type="button"
            className={styles.uploadAction}
            onClick={() => {
              onResume(upload.uploadId);
            }}
          >
            {upload.state === 'FAILED' ? t('upload.retry') : t('upload.resume')}
          </button>
        ) : null}
        <button
          type="button"
          className={`${styles.uploadAction} ${styles.destructive}`}
          onClick={() => {
            onCancel(upload.uploadId);
          }}
        >
          {upload.state === 'READY' ? t('upload.remove') : t('upload.cancel')}
        </button>
      </div>
    </li>
  );
}
